import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { getConfig, googleSignIn } from "@/lib/api";

const GSI_SRC = "https://accounts.google.com/gsi/client";

function loadGoogleScript() {
    return new Promise((resolve, reject) => {
        if (window.google?.accounts?.id) {
            resolve();
            return;
        }
        const existing = document.querySelector(`script[src="${GSI_SRC}"]`);
        if (existing) {
            existing.addEventListener("load", () => resolve());
            existing.addEventListener("error", reject);
            return;
        }
        const script = document.createElement("script");
        script.src = GSI_SRC;
        script.async = true;
        script.defer = true;
        script.onload = () => resolve();
        script.onerror = reject;
        document.head.appendChild(script);
    });
}

export default function AdminLogin() {
    const [localError, setLocalError] = useState("");
    const [ready, setReady] = useState(false);
    const buttonRef = useRef(null);
    const { user, loading, refresh } = useAuth();
    const nav = useNavigate();

    useEffect(() => {
        let cancelled = false;

        const handleCredentialResponse = async (response) => {
            setLocalError("");
            try {
                await googleSignIn(response.credential);
                await refresh();
                nav("/admin", { replace: true });
            } catch (err) {
                if (err?.response?.status === 403) {
                    nav("/admin/denied", { replace: true });
                    return;
                }
                setLocalError("Sign-in failed. Please try again.");
            }
        };

        // The client id comes from the API rather than a build-time variable,
        // so it always matches the value the server verifies tokens against.
        Promise.all([loadGoogleScript(), getConfig()])
            .then(([, config]) => {
                if (cancelled) return;

                const clientId = config?.google_client_id;
                if (!clientId) {
                    setLocalError(
                        "Sign-in is not configured on this deployment (missing Google client ID).",
                    );
                    return;
                }

                window.google.accounts.id.initialize({
                    client_id: clientId,
                    callback: handleCredentialResponse,
                });
                if (buttonRef.current) {
                    window.google.accounts.id.renderButton(buttonRef.current, {
                        theme: "outline",
                        size: "large",
                        width: 320,
                        text: "signin_with",
                    });
                }
                setReady(true);
            })
            .catch(() => {
                if (!cancelled) {
                    setLocalError("Couldn't load Google Sign-In. Check your connection.");
                }
            });

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!loading && user?.is_admin) {
        window.location.replace("/admin");
        return null;
    }

    return (
        <div className="min-h-screen bg-[#F6F3EC] text-[#1B1B1B] flex flex-col">
            <div className="mx-auto max-w-md w-full px-6 py-16 flex-1 flex flex-col justify-center">
                <p className="eyebrow text-[#1B1B1B]/60 mb-6">§ Admin — 00</p>
                <h1 className="font-serif-editorial text-5xl md:text-6xl leading-[0.98] tracking-[-0.02em] mb-6">
                    Sign in to <em className="italic text-[#A8844F]">Depmont.</em>
                </h1>
                <p className="font-sans-tight text-[#1B1B1B]/75 text-base leading-[1.6] mb-10 max-w-sm font-light">
                    The studio console is restricted to authorised Depmont Google accounts.
                </p>

                {localError && (
                    <div className="mb-8 border border-[#1B1B1B] p-4 text-sm font-sans-tight text-red-800">
                        {localError}
                    </div>
                )}

                <div ref={buttonRef} />
                {!ready && !localError && (
                    <p className="eyebrow text-[#1B1B1B]/50 mt-2">Loading Google Sign-In…</p>
                )}

                <a href="/" className="mt-10 eyebrow text-[#1B1B1B]/60 link-underline self-start">
                    ← Back to depmont.com
                </a>
            </div>
        </div>
    );
}
